import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSocketContext } from '@/contexts/socket-context'
import { useAuthenticatedClient, queryKeys } from '@/api/queries'
import type { TaskEvent } from '@/hooks/use-socket'

const USE_MOCK_AP_INVOICES = import.meta.env.DEV && import.meta.env.VITE_MOCK_AP_INVOICES === '1'

export interface ActivityTask {
  uuid: string
  name: string
  status: string
  progress?: number | null
  entity_type?: string | null
  entity_id?: number | null
  entity_label?: string | null
  created_at?: string
  updated_at?: string
}

const RECENT_TTL_MS = 5 * 60 * 1000

export function useActivity() {
  const { isConnected, on } = useSocketContext()
  const [activeTasks, setActiveTasks] = useState<ActivityTask[]>([])
  const [recentTasks, setRecentTasks] = useState<ActivityTask[]>([])
  const cleanupTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // REST endpoint for initial state + polling fallback
  const getClient = useAuthenticatedClient()
  const activityQuery = useQuery({
    queryKey: queryKeys.activity.all,
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return { active: [], recent: [] }
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/activity')
      if (error) throw error
      return data
    },
    enabled: !USE_MOCK_AP_INVOICES,
    // Poll every 10s when socket is disconnected, disable when connected
    refetchInterval: isConnected ? false : 10_000,
    refetchIntervalInBackground: false,
  })

  // Hydrate from REST response
  useEffect(() => {
    if (!activityQuery.data) return
    setActiveTasks(activityQuery.data.active)
    setRecentTasks(activityQuery.data.recent)
  }, [activityQuery.data])

  // Re-fetch initial state on socket reconnect
  useEffect(() => {
    if (isConnected) {
      activityQuery.refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected])

  // Socket event handlers
  const handleQueued = useCallback((data: TaskEvent) => {
    setActiveTasks((prev) => {
      if (prev.some((t) => t.uuid === data.uuid)) return prev
      return [data, ...prev]
    })
  }, [])

  const handleStarted = useCallback((data: TaskEvent) => {
    setActiveTasks((prev) => {
      const exists = prev.some((t) => t.uuid === data.uuid)
      if (exists) {
        return prev.map((t) => (t.uuid === data.uuid ? { ...t, ...data } : t))
      }
      return [data, ...prev]
    })
  }, [])

  const handleDone = useCallback((data: TaskEvent) => {
    setActiveTasks((prev) => prev.filter((t) => t.uuid !== data.uuid))
    setRecentTasks((prev) => {
      if (prev.some((t) => t.uuid === data.uuid)) {
        return prev.map((t) => (t.uuid === data.uuid ? { ...t, ...data } : t))
      }
      return [data, ...prev]
    })
  }, [])

  useEffect(() => {
    if (USE_MOCK_AP_INVOICES) return
    const unsub1 = on('task:queued', handleQueued)
    const unsub2 = on('task:started', handleStarted)
    const unsub3 = on('task:completed', handleDone)
    const unsub4 = on('task:failed', handleDone)
    return () => {
      unsub1()
      unsub2()
      unsub3()
      unsub4()
    }
  }, [on, handleQueued, handleStarted, handleDone])

  // Clean up old recent tasks every 30s
  useEffect(() => {
    cleanupTimerRef.current = setInterval(() => {
      const cutoff = Date.now() - RECENT_TTL_MS
      setRecentTasks((prev) =>
        prev.filter((t) => {
          if (!t.updated_at) return true
          return new Date(t.updated_at).getTime() > cutoff
        }),
      )
    }, 30_000)

    return () => {
      if (cleanupTimerRef.current) clearInterval(cleanupTimerRef.current)
    }
  }, [])

  return { activeTasks, recentTasks, isConnected }
}
